import { useEffect, useLayoutEffect, useRef, type FormEvent } from 'react'
import HoldToTransmit from './HoldToTransmit'
import styles from './Compose.module.css'
import { useT } from '../../../lib/i18n'

/**
 * THE COMPOSE SURFACE — writing, on a phone.
 *
 * The failure this replaces is not the keyboard shifting the screen (that is
 * `useKeyboardInset`'s job). It is that a one-line input leaves the DRAFT
 * illegible: 380px of usable screen spent on transcript you are not reading,
 * while the thing you are actually composing gets forty pixels of it. Inverted
 * here — the draft takes the room, and context is CURATED rather than shrunk:
 * the last thing he sent, pinned, because that is what you are answering.
 *
 * TYPING IS A FIRST-CLASS PATH, not a fallback for when dictation fails. Voice is
 * unusable around other people whatever its quality, and its quality is per
 * language — Icelandic speech-to-text is poor, and `is` ships. More decisively,
 * this is an ENGINE surface: `CorrespondenceCoordinator`'s shared-page letters
 * format has the player writing paragraphs, and a single-line box is already
 * broken there before any keyboard opens.
 *
 * THREE RULES:
 *
 *  1. NEVER A TRAP (§4.3). Dismissing the keyboard leaves compose; sending leaves
 *     compose; the draft survives both. There is no state to get stuck in and
 *     nothing that disables the composer.
 *  2. IT SCALES WITH THE FORMAT. The field grows to its content — one line for
 *     "copy", many for a letter — so the same component serves radio traffic and
 *     Carnuntum without either being told what size it is.
 *  3. IT NEVER WRITES CONTENT. The pinned context is the feed's own last inbound
 *     line, reproduced; nothing here composes, summarizes or labels it.
 */
export default function Compose({
  value,
  onChange,
  onSend,
  onExit,
  answering,
  disabled,
}: {
  value: string
  onChange: (text: string) => void
  onSend: () => void
  /** The keyboard went away, or the draft went out. Back to the transcript. */
  onExit: () => void
  /** The last thing he sent — what this transmission answers. Absent is fine. */
  answering?: string | null
  disabled?: boolean
}) {
  const t = useT()
  const field = useRef<HTMLTextAreaElement>(null)

  // Grow to the content rather than declaring a height. Runs before paint so the
  // field never renders at the wrong size and settles a frame later.
  useLayoutEffect(() => {
    const el = field.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])

  useEffect(() => {
    field.current?.focus()
  }, [])

  function submit(ev: FormEvent) {
    ev.preventDefault()
    if (!value.trim() || disabled) return
    onSend()
  }

  return (
    <form className={styles.compose} onSubmit={submit}>
      {answering && (
        <div className={styles.answering}>
          <span className={styles.answeringLabel}>{t('desk.compose.answering')}</span>
          <p className={styles.answeringText}>{answering}</p>
        </div>
      )}

      <textarea
        ref={field}
        className={styles.field}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // Blur is the keyboard going away — the one signal iOS gives reliably for
        // "done writing". Leaving compose on it is what keeps this from being a
        // mode: the way out is the way you would already dismiss a keyboard.
        onBlur={onExit}
        onKeyDown={(e) => {
          // Enter sends; Shift+Enter is a newline, so a letter can have paragraphs.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            submit(e)
          }
          if (e.key === 'Escape') onExit()
        }}
        placeholder={t('desk.compose.key')}
        aria-label={t('desk.compose.keyAria')}
        rows={1}
        disabled={disabled}
      />

      <div className={styles.acts}>
        <HoldToTransmit
          disabled={disabled}
          onTranscript={(t) => onChange(value.trim() ? `${value.trim()} ${t}` : t)}
        />
        <button
          type="submit"
          className={styles.send}
          disabled={disabled || !value.trim()}
        >
          SEND
        </button>
      </div>
    </form>
  )
}
